import {
    createSelector,
    createSlice,
} from '@reduxjs/toolkit';
import {groupBy, set} from 'lodash-es';

import {TAppState} from 'store/config/types';

import {DRILLDOWN_DFF_TABLE_NAME, DESCRIPTIVE_FLEXFIELDS_SLICE} from './descriptive-flexfields-slice-constants';
import {
    IDescriptiveFlexfieldSegment,
    TDescriptiveFlexfieldsSliceState,
    ISelectDescriptiveFlexfieldSegmentsArgs,
    TSelectDrillDownDFFSegmentsArgs,
} from './descriptive-flexfields-slice-types';
import {loadContextSegments, loadDDContextSegments} from './descriptive-flexfields-thunks';

export const descriptiveFlexfieldsSlice = createSlice({
    initialState: {
        descriptiveFlexfields: {},
    } as TDescriptiveFlexfieldsSliceState,
    name: DESCRIPTIVE_FLEXFIELDS_SLICE,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(loadDDContextSegments.fulfilled, (state, {meta, payload}) => {
            const {arg} = meta;
            const {contextCode} = arg;
            set(state, ['descriptiveFlexfields', 'contextSegments', DRILLDOWN_DFF_TABLE_NAME, contextCode], payload);
        });
        builder.addCase(loadContextSegments.fulfilled, (state, {meta, payload}) => {
            const {arg} = meta;
            const {contextCode, dffName} = arg.params;
            set(state, ['descriptiveFlexfields', 'contextSegments', dffName, contextCode], payload);
        });
    },
});

const selectDescriptiveFlexfieldsSliceState = (state: TAppState) => state[DESCRIPTIVE_FLEXFIELDS_SLICE];

export const selectContextSegments = createSelector(
    [
        selectDescriptiveFlexfieldsSliceState,
        (_, args: ISelectDescriptiveFlexfieldSegmentsArgs) => args,
    ],
    (state, {contextCode, dffName}) => state.descriptiveFlexfields.contextSegments?.[dffName]?.[contextCode],
);

export const selectDDContextSegments = createSelector(
    [
        selectDescriptiveFlexfieldsSliceState,
        (_, args: TSelectDrillDownDFFSegmentsArgs) => args,
    ],
    (state, {contextCode}) => state.descriptiveFlexfields.contextSegments?.[DRILLDOWN_DFF_TABLE_NAME]?.[contextCode],
);

export const selectDDContextSegmentsGroupedByAttribute = createSelector(
    [
        selectDDContextSegments,
        (_, args: TSelectDrillDownDFFSegmentsArgs & {toLowerCase?: boolean}) => args,
    ],
    (segments, {toLowerCase}) => {
        if (!segments) return segments;
        const groupedSegments = groupBy(segments, 'attributeColumnName');
        const groupedSingleSegments = Object.fromEntries(
            Object.entries(groupedSegments).map(([attributeName, values]) => {
                const atrName = toLowerCase ? attributeName.toLocaleLowerCase() : attributeName;
                const firstValue = values?.[0];
                return [atrName, firstValue];
            }).filter(([, value]) => !!value),
        );
        return groupedSingleSegments as {[attributeName: string]: IDescriptiveFlexfieldSegment};
    },
);

export const descriptiveFlexfieldsSelectors = {
    selectContextSegments,
};

export const {
    reducer: descriptiveFlexfieldsReducer,
    actions: descriptiveFlexfieldsActions,
} = descriptiveFlexfieldsSlice;
